import { BreakpointObserver } from '@angular/cdk/layout';
import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChild,
  effect,
  ElementRef,
  inject,
  input,
  linkedSignal,
  model,
  OnInit,
  output,
  TemplateRef,
  untracked,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import type { ColumnDef } from '@tanstack/angular-table';
import { createAngularTable, FlexRenderDirective, getCoreRowModel, getSortedRowModel } from '@tanstack/angular-table';
import { injectVirtualizer } from '@tanstack/angular-virtual';
import type {
  Column,
  Row,
  RowData,
  RowSelectionState,
  SortingState,
  Table,
  TableState,
  Updater,
  VisibilityState,
} from '@tanstack/table-core';
import type { VirtualItem } from '@tanstack/virtual-core';
import { LucideChevronDown, LucideChevronUp, LucideDynamicIcon, LucidePin, LucidePinOff } from '@lucide/angular';
import { map } from 'rxjs';
import { LoaderComponent } from '../../atoms/loader/loader.component';
import { PaginationComponent } from '../../atoms/pagination/pagination.component';
import { CheckboxComponent } from '../../atoms/checkbox/checkbox.component';
import { TextDirective } from '../../typography/text.directive';
import { TableColumnFilterComponent } from './table-column-filter.component';
import type { TableColumnFilter, TableFilterChange, TableSortChange, TableSortDirection } from './table.types';

export type TableVariant = 'responsive' | 'table';
export type TablePaginationType = 'paginator' | 'continuous-scroll';

@Component({
  selector: 'joii-table',
  standalone: true,
  imports: [
    FlexRenderDirective,
    TextDirective,
    PaginationComponent,
    LucideDynamicIcon,
    NgTemplateOutlet,
    LoaderComponent,
    CheckboxComponent,
    TableColumnFilterComponent,
  ],
  templateUrl: './table.component.html',
  styleUrl: './table.component.css',

  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableComponent<TData extends RowData> implements OnInit {
  breakpointObserver = inject(BreakpointObserver);
  /** Table data rows */
  readonly data = input.required<TData[]>();

  /** Column definitions (accessorKey, header, cell, footer, etc.) */
  readonly columns = input.required<ColumnDef<TData, unknown>[]>();

  /** Whether to show the table header. Default: true */
  readonly showHeader = input<boolean>(true);

  /** Row indices (0-based) to highlight with the design highlight style */
  readonly highlightedRowIndices = input<number[]>([]);

  /** Row indices (0-based) to highlight with the design error style */
  readonly errorHighlightedRowIndices = input<number[]>([]);

  /** Enable debug mode for table state. Default: false */
  readonly debugTable = input<boolean>(false);

  readonly totalPages = input<number>(0);

  /** Pagination UI: numbered pages or load-more on scroll. Default: paginator */
  readonly paginationType = input<TablePaginationType>('paginator');

  /**
   * When true, shows a loader below the last row.
   * For continuous scroll, also blocks requesting the next page. Default: false
   */
  readonly loading = input<boolean>(false);

  /** Force virtual scroll on/off. When unset, auto-enables for continuous scroll or large lists. */
  readonly virtualScroll = input<boolean | undefined>(undefined);

  /** Row count at which virtual scroll auto-enables. Default: 50 */
  readonly virtualScrollThreshold = input<number>(50);

  /** Fixed height of the virtual scroll container (CSS length). Default: 32rem */
  readonly virtualScrollHeight = input<string>('32rem');

  /** Estimated row height in pixels for virtual scroll positioning. Default: 52 */
  readonly virtualRowHeight = input<number>(52);

  /** Column ids to pin left on first render. Pin state is then controlled via header icons. */
  readonly initialPinnedColumns = input<string[]>([]);
  /** Column ids to pin right on first render, e.g. an actions column. */
  readonly initialRightPinnedColumns = input<string[]>([]);
  /** Show pin/unpin controls in column headers. Default: false */
  readonly enableColumnPinning = input<boolean>(false);

  readonly variant = input<TableVariant>('responsive');

  /**
   * Server-driven sorting (TanStack `manualSorting`). Rows are not sorted client-side and only
   * columns with `enableSorting: true` are sortable. Header clicks emit `sortChange`. Default: false
   */
  readonly manualSorting = input<boolean>(false);
  /** Currently sorted column id (manual sorting). */
  readonly sortColumn = input<string | null | undefined>(undefined);
  /** Current sort direction (manual sorting). Default: 'asc' */
  readonly sortDirection = input<TableSortDirection>('asc');
  /** Emitted on header click when `manualSorting` is on. Same column toggles; a new column starts at 'asc'. */
  readonly sortChange = output<TableSortChange>();

  /** Emitted when a header filter (`columnDef.meta.filter`) changes. */
  readonly filterChange = output<TableFilterChange>();

  /** Show a leading checkbox column with select-all. Default: false */
  readonly enableRowSelection = input<boolean>(false);
  /** Selection state keyed by row id. Two-way bindable; set to `{}` to clear. */
  readonly rowSelection = model<RowSelectionState>({});
  /** Stable row id (recommended with selection on server-paged data). Defaults to the row index. */
  readonly getRowId = input<((row: TData, index: number) => string) | undefined>(undefined);
  /** Emitted with the selected row objects whenever selection changes from the UI. */
  readonly selectedRowsChange = output<TData[]>();

  /** Column visibility (TanStack `columnVisibility`), e.g. `{ email: false }`. */
  readonly columnVisibility = input<VisibilityState | undefined>(undefined);

  /** Message shown when `data` is empty and not loading. */
  readonly emptyMessage = input<string | undefined>(undefined);
  readonly emptyStateTemplate = contentChild<TemplateRef<unknown>>('emptyStateTemplate');
  readonly showEmptyState = computed(
    () => this.data().length === 0 && !this.loading() && (!!this.emptyMessage() || !!this.emptyStateTemplate())
  );

  /** Current filter values keyed by column id, seeded from `meta.filter.value`. */
  readonly filterValues = linkedSignal<Record<string, unknown>>(() => {
    const values: Record<string, unknown> = {};
    for (const column of this.columns()) {
      const filter = column.meta?.filter;
      const id = this.getColumnDefId(column);
      if (filter && id) {
        values[id] = filter.value ?? null;
      }
    }
    return values;
  });

  /** Local sort state for manual sorting, reset whenever the inputs change. */
  private readonly manualSortState = linkedSignal<SortingState>(() => {
    const id = this.sortColumn();
    return id ? [{ id, desc: this.sortDirection() === 'desc' }] : [];
  });
  readonly cardHeaderTemplate = contentChild<TemplateRef<{ row: Row<any> }>>('cardHeaderTemplate');
  readonly cardContentTemplate = contentChild<TemplateRef<{ row: Row<any> }>>('cardContentTemplate');
  readonly cardFooterTemplate = contentChild<TemplateRef<{ row: Row<any> }>>('cardFooterTemplate');
  readonly isLargeScreen = toSignal(
    this.breakpointObserver.observe('(min-width: 1024px)').pipe(map((res) => res.matches)),
    { initialValue: true }
  );

  displayMode = computed(() =>
    this.variant() === 'responsive' ? (this.isLargeScreen() ? 'table' : 'cards') : this.variant()
  );

  /** Current page for pagination. Supports two-way binding: [(page)]="currentPage" */
  readonly page = model<number>(1);

  readonly canLoadMore = computed(
    () => this.paginationType() === 'continuous-scroll' && this.page() < this.totalPages() && !this.loading()
  );

  readonly rows = computed(() => this.table.getRowModel().rows);

  readonly useVirtualScroll = computed(() => {
    const explicit = this.virtualScroll();
    if (explicit !== undefined) {
      return explicit;
    }
    return this.paginationType() === 'continuous-scroll' || this.data().length > this.virtualScrollThreshold();
  });

  readonly virtualTableWidth = computed(
    () =>
      this.table.getVisibleLeafColumns().reduce((total, column) => total + column.getSize(), 0) +
      (this.enableRowSelection() ? TableComponent.SELECT_COLUMN_WIDTH : 0)
  );

  /** Width of the row-selection checkbox column in px. */
  static readonly SELECT_COLUMN_WIDTH = 56;
  readonly selectColumnWidth = TableComponent.SELECT_COLUMN_WIDTH;

  private readonly scrollElement = viewChild<ElementRef<HTMLDivElement>>('scrollElement');
  private readonly headerScrollElement = viewChild<ElementRef<HTMLDivElement>>('headerScrollElement');
  private readonly scrollSentinel = viewChild<ElementRef<HTMLElement>>('scrollSentinel');

  readonly virtualizer = injectVirtualizer(() => ({
    scrollElement: this.useVirtualScroll() ? this.scrollElement() : undefined,
    count: this.useVirtualScroll() ? this.rows().length : 0,
    estimateSize: () => this.virtualRowHeight(),
    overscan: 20,
    // Signal updates already drive CD; appRef.tick() nests into the current
    // change-detection cycle and throws NG0101 (recursive tick).
    useApplicationRefTick: false,
  }));

  readonly onRowClicked = output<number>();
  /** Emitted with the clicked row's data object. */
  readonly rowClick = output<TData>();
  readonly chevronUpIcon = LucideChevronUp;
  readonly chevronDownIcon = LucideChevronDown;
  readonly pinIcon = LucidePin;
  readonly pinOffIcon = LucidePinOff;

  /**
   * Created in `ngOnInit` so TanStack's `queueMicrotask` init cannot read required
   * `data`/`columns` before Angular has bound them (NG0950).
   * @internal
   */
  table!: Table<TData>;

  constructor() {
    // Re-apply pinning when the inputs change after creation (e.g. fewer pinned columns on small screens).
    effect(() => {
      const left = this.initialPinnedColumns();
      const right = this.initialRightPinnedColumns();
      if (!this.table) {
        return;
      }
      untracked(() => this.table.setColumnPinning({ left, right }));
    });

    effect((onCleanup) => {
      const body = this.scrollElement()?.nativeElement;
      const header = this.headerScrollElement()?.nativeElement;
      if (!body || !header || !this.useVirtualScroll()) {
        return;
      }

      const syncHeaderScroll = () => {
        header.scrollLeft = body.scrollLeft;
      };
      syncHeaderScroll();
      body.addEventListener('scroll', syncHeaderScroll, { passive: true });
      onCleanup(() => body.removeEventListener('scroll', syncHeaderScroll));
    });

    effect((onCleanup) => {
      const sentinel = this.scrollSentinel();
      if (!sentinel || this.paginationType() !== 'continuous-scroll') {
        return;
      }

      const root = this.useVirtualScroll() ? (this.scrollElement()?.nativeElement ?? null) : null;
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting) || !this.canLoadMore()) {
            return;
          }
          // Defer so we do not write during CD / effect / IntersectionObserver sync delivery.
          queueMicrotask(() => {
            if (this.canLoadMore()) {
              this.page.update((currentPage) => currentPage + 1);
            }
          });
        },
        { root, rootMargin: '200px' }
      );

      observer.observe(sentinel.nativeElement);
      onCleanup(() => observer.disconnect());
    });
  }

  /**
   * TanStack Virtual table row offset.
   * @see https://tanstack.com/virtual/latest/docs/framework/angular/examples/table
   */
  getVirtualRowTransform(virtualRow: VirtualItem, renderedIndex: number): string {
    return `translateY(${virtualRow.start - renderedIndex * virtualRow.size}px)`;
  }

  getColumnStyle(column: Column<TData, unknown>, useLegacyWidth: boolean): Record<string, string | undefined> {
    const pinning = this.getPinningOffset(column);
    if (useLegacyWidth) {
      const size = column.getSize();
      return size !== 150 ? { width: `${size}px`, ...pinning } : pinning;
    }
    return pinning;
  }

  /** Check if a row at the given index should be highlighted */
  isRowHighlighted(index: number): boolean {
    return this.highlightedRowIndices().includes(index);
  }

  /** Check if a row at the given index should be error-highlighted */
  isRowErrorHighlighted(index: number): boolean {
    return this.errorHighlightedRowIndices().includes(index);
  }

  /** Get the original data index for a row (stable across sorting/filtering) */
  getOriginalRowIndex(row: { id: string; index?: number }): number {
    return row.index ?? parseInt(row.id, 10);
  }

  handleRowClick(row: Row<TData>): void {
    this.onRowClicked.emit(this.getOriginalRowIndex(row));
    this.rowClick.emit(row.original);
  }

  handleHeaderClick(column: Column<TData, unknown>, event: MouseEvent): void {
    if (!column.getCanSort()) {
      return;
    }
    if (!this.manualSorting()) {
      column.getToggleSortingHandler()?.(event);
      return;
    }
    const current = this.manualSortState()[0];
    const direction: TableSortDirection = current?.id === column.id && !current.desc ? 'desc' : 'asc';
    this.manualSortState.set([{ id: column.id, desc: direction === 'desc' }]);
    this.sortChange.emit({ columnId: column.id, direction });
  }

  getColumnFilter(column: Column<TData, unknown>): TableColumnFilter | undefined {
    return column.columnDef.meta?.filter;
  }

  isWrapColumn(column: Column<TData, unknown>): boolean {
    return column.columnDef.meta?.wrap === true;
  }

  onFilterChange(columnId: string, value: unknown): void {
    const filters = { ...this.filterValues(), [columnId]: value };
    this.filterValues.set(filters);
    this.filterChange.emit({ columnId, value, filters });
  }

  toggleAllRows(checked: boolean): void {
    this.table.toggleAllRowsSelected(checked);
    this.emitSelectedRows();
  }

  toggleRow(row: Row<TData>, checked: boolean): void {
    row.toggleSelected(checked);
    this.emitSelectedRows();
  }

  private emitSelectedRows(): void {
    this.selectedRowsChange.emit(this.table.getSelectedRowModel().rows.map((row) => row.original));
  }

  private getColumnDefId(column: ColumnDef<TData, unknown>): string | undefined {
    if (column.id) {
      return column.id;
    }
    const accessorKey = (column as { accessorKey?: unknown }).accessorKey;
    return typeof accessorKey === 'string' ? accessorKey.replace(/\./g, '_') : undefined;
  }

  private buildControlledState(): Partial<TableState> {
    const state: Partial<TableState> = {};
    if (this.manualSorting()) {
      state.sorting = this.manualSortState();
    }
    if (this.enableRowSelection()) {
      state.rowSelection = this.rowSelection();
    }
    const visibility = this.columnVisibility();
    if (visibility) {
      state.columnVisibility = visibility;
    }
    return state;
  }

  /** Get the header display label (name) for a column, falling back to column id */
  getHeaderLabel(header: { id: string; column: { columnDef: { header?: unknown } } }): string {
    const def = header.column.columnDef.header;
    return typeof def === 'string' ? def : header.id;
  }

  /** Dynamic left/right offsets for pinned columns (sticky positioning is in CSS) */
  getPinningOffset(column: Column<TData, unknown>): Record<string, string | undefined> {
    const isPinned = column.getIsPinned();
    if (!isPinned) {
      return {};
    }

    return {
      left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
      right: isPinned === 'right' ? `${column.getAfter('right')}px` : undefined,
    };
  }

  isLastLeftPinnedColumn(column: Column<TData, unknown>): boolean {
    return column.getIsPinned() === 'left' && column.getIsLastColumn('left');
  }

  isFirstRightPinnedColumn(column: Column<TData, unknown>): boolean {
    return column.getIsPinned() === 'right' && column.getIsFirstColumn('right');
  }

  toggleColumnPin(column: Column<TData, unknown>, event: MouseEvent): void {
    event.stopPropagation();
    column.pin(column.getIsPinned() ? false : 'left');
  }

  ngOnInit(): void {
    this.table = createAngularTable(() => ({
      data: this.data(),
      columns: this.columns(),
      defaultColumn: {
        size: 150,
        minSize: 80,
        // Manual (server) sorting is opt-in per column via `enableSorting: true`.
        ...(this.manualSorting() ? { enableSorting: false } : {}),
      },
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
      debugTable: this.debugTable(),
      ...(this.manualSorting() ? { manualSorting: true } : {}),
      ...(this.getRowId() ? { getRowId: this.getRowId() } : {}),
      enableRowSelection: this.enableRowSelection(),
      onRowSelectionChange: (updater: Updater<RowSelectionState>) => {
        this.rowSelection.update((current) => (typeof updater === 'function' ? updater(current) : updater));
      },
      state: this.buildControlledState(),
      initialState: {
        columnPinning: {
          left: this.initialPinnedColumns(),
          right: this.initialRightPinnedColumns(),
        },
      },
    })) as unknown as Table<TData>;
  }
}
